import {
  Auth, browserLocalPersistence, browserSessionPersistence, Dependencies, indexedDBLocalPersistence, PopupRedirectResolver,
} from 'firebase/auth';

/**
 * #6743: the web build creates Firebase Auth WITHOUT the popup sign-in resolver, so that
 * start-up never waits for Google's sign-in scripts and hidden sign-in frame. Firebase loads
 * that frame early on Safari, iOS and mobile browsers on purpose: a popup that opens seconds
 * after the tap can be blocked by Safari. So the app loads the frame itself, as soon as a
 * screen with a sign-in button opens. The public API has no call for that, so this reaches
 * Firebase's internal `_initialize`. See design-docs/boot-sequence.md, "#6743".
 */

/**
 * How the web build creates Firebase Auth: the same storage `getAuth()` uses, but WITHOUT the
 * popup sign-in resolver. With the resolver attached, Firebase loads Google's sign-in scripts and
 * frame on Safari, iOS and mobile browsers before it reads the stored user, and a slow network
 * then held the whole app start-up. Popup sign-in passes the resolver itself.
 */
export function webAuthDependencies(): Dependencies {
  return { persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence] };
}

/** The one internal method the warm-up needs. Firebase's own internal type has more. */
export interface WarmablePopupResolver {
  _initialize(auth: Auth): Promise<unknown>;
}

/** Where the warm-up stands right now. */
export enum PopupResolverWarmupState {
  /** nothing has asked yet, or the last load failed and the next call starts a new one */
  NotStarted = 'NotStarted',
  Loading = 'Loading',
  Ready = 'Ready',
  /** the internal function is not there; the warm-up will never run in this app instance */
  Unavailable = 'Unavailable',
}

export enum PopupResolverWarmupOutcome {
  /** the frame is loaded: a tap now opens the popup straight away */
  Ready = 'Ready',
  /** the internal function is not there (a Firebase upgrade changed it); sign-in loads the frame on tap, as before */
  Unavailable = 'Unavailable',
  /** loading the frame failed; Firebase forgets the attempt, so the next warm-up or tap tries again */
  Failed = 'Failed',
}

export interface PopupResolverWarmupResult {
  outcome: PopupResolverWarmupOutcome;
  /** set when the outcome is Failed */
  error?: unknown;
}

/** What a tap on a social sign-in button does on web. */
export enum SignInTapGate {
  /** open the popup now */
  OpenPopup = 'OpenPopup',
  /** the frame was still loading and has just finished: ask for a second tap, which opens the popup at once */
  AskToTapAgain = 'AskToTapAgain',
}

/** The two AuthService calls the tap gate needs. */
export interface SignInPopupWarmer {
  signInPopupStillLoading(): boolean;
  warmSignInPopupAsync(): Promise<boolean>;
}

/**
 * Decides what a tap on a social sign-in button does. If the frame is still loading, a popup
 * opened after waiting for it can be blocked by Safari, so the tap waits for the frame and asks
 * for a second tap. If the frame then still did not load, the tap opens the popup anyway, which
 * loads the frame itself as sign-in always did before the warm-up.
 */
export async function gateSignInTapAsync(warmer: SignInPopupWarmer): Promise<SignInTapGate> {
  if (!warmer.signInPopupStillLoading()) return SignInTapGate.OpenPopup;
  const ready = await warmer.warmSignInPopupAsync();
  return ready ? SignInTapGate.AskToTapAgain : SignInTapGate.OpenPopup;
}

/**
 * The resolver instance that `signInWithPopup` will use, or null when the warm-up cannot reach it.
 *
 * `publicResolver` is `browserPopupRedirectResolver` from `firebase/auth`, the one the app passes
 * to `signInWithPopup`. `internalResolver` and `getInstance` come from `@firebase/auth/internal`.
 * Firebase keeps one instance per resolver class, so warming the instance of the SAME class is what
 * makes a later tap find the frame loaded. If the bundle ever held two copies of `@firebase/auth`,
 * the two classes would differ and the warm-up would load a frame nobody uses, so that case
 * answers null.
 */
export function findWarmablePopupResolver(
  publicResolver: PopupRedirectResolver,
  internalResolver: PopupRedirectResolver,
  getInstance: (cls: unknown) => unknown,
): WarmablePopupResolver | null {
  if (publicResolver !== internalResolver) return null;
  const instance = getInstance(internalResolver) as Partial<WarmablePopupResolver> | null | undefined;
  if (typeof instance?._initialize !== 'function') return null;
  return instance as WarmablePopupResolver;
}
